import { findItemT, findPayloadT } from '@execonline-inc/collections';
import { stringLiteral } from '@execonline-inc/decoders';
import { always, assertNever } from '@kofno/piper';
import { BadStatus } from 'ajaxian';
import { Decoder, field, oneOf, succeed } from 'jsonous';
import { just, Maybe, nothing } from 'maybeasy';
import { Task } from 'taskarian';
import { callApi, postToApi } from '../../Appy';
import { programRoute } from '../../ClientRoutes';
import ErrorActionableReaction, { EAProps, handleError } from '../../ErrorActionableReaction';
import { warnAndNotify } from '../../Honeybadger';
import { findLink, findLinkT, MissingLinkError } from '../../LinkyLinky';
import { nav } from '../../Navigation';
import { programsStore } from '../../ProgramsStore';
import { ProgramResource } from '../../ProgramsStore/Types';
import { getProgram, Program, whenActive, whenUpcoming } from '../../ProgramStore/Types';
import { linksDecoder } from '../../Resource/Decoders';
import { Link } from '../../Resource/Types';
import SegmentStore from '../../SegmentStore';
import { quizResultDecoder, segmentResourceDecoder } from '../../SegmentStore/Decoders';
import {
  QuizResult,
  SegmentNavigationDirectionType,
  SegmentResource,
  SegmentState,
  SegmentSubmissionParams,
  TeamDiscussionSubmissionParams,
} from '../../SegmentStore/Types';
import { Upload, Uploads } from '../../Uploads';
import { FormFieldOutput } from '../EmbeddedFormFieldAsset/Types';
import { dataLayerUpdate, firstPushFor } from '../Tooling/GoogleTagManagerTooling/Loader';
import { percentCompleteBucket } from '../Tooling/GoogleTagManagerTooling/Types';
import { AdvanceHook, SegmentReactionErrors, SubmissionError } from './Types';

interface Props extends EAProps<SegmentStore> {
  store: SegmentStore;
  params: Params;
  advanceHook: AdvanceHook;
}

interface Params {
  programId: string;
  moduleId: string;
  segmentId: string;
}

// The file is already in S3 by the time a segment is submitted, so the
// submission names the ActiveStorage blob rather than a temporary path the
// server then has to copy.
interface UploadOutput {
  signed_id: string;
  filename: string;
}

interface AssignmentDueOutput {
  type: 'assignment-due';
  files: UploadOutput[];
}

interface TeamDiscussionOutput {
  type: 'team-discussion';
  files: UploadOutput[];
  team_discussion: {
    title: string;
    content: string;
  };
}

interface OverviewOutput {
  type: 'overview';
  fields: FormFieldOutput[];
}

export type SubmissionOutput = AssignmentDueOutput | TeamDiscussionOutput | OverviewOutput;

const segmentEndPoint = callApi(segmentResourceDecoder, {});

interface SubmitSuccessResponse {
  kind: 'submit-success';
  segmentResource: SegmentResource;
}

interface QuizFailedResponse {
  kind: 'quiz-failed';
  quizResult: QuizResult;
  links: ReadonlyArray<Link>;
}

interface UnsupportedFileFormatResponse {
  kind: 'unsupported-file-format';
}

type SubmitResponse = SubmitSuccessResponse | QuizFailedResponse | UnsupportedFileFormatResponse;

// The server pre-checks action-plan uploads against the AI-feedback allowlist
// (ActionPlanFileDecorator) and responds with this structured error payload
// when a file's extension is unsupported (DVLP-7429).
const unsupportedFileFormatResponseDecoder: Decoder<SubmitResponse> = field(
  'payload',
  field('error', stringLiteral('unsupported_file_format')),
).map(always<SubmitResponse>({ kind: 'unsupported-file-format' }));

const quizFailedResponseDecoder: Decoder<SubmitResponse> = succeed({})
  .assign('quizResult', field('payload', field('quiz_result', quizResultDecoder)))
  .assign('links', field('links', linksDecoder))
  .map(
    ({ quizResult, links }): SubmitResponse => ({
      kind: 'quiz-failed',
      quizResult,
      links,
    }),
  );

const submitSuccessResponseDecoder: Decoder<SubmitResponse> = segmentResourceDecoder.map(
  (sr): SubmitResponse => ({
    kind: 'submit-success',
    segmentResource: sr,
  }),
);

const submitResponseDecoder: Decoder<SubmitResponse> = oneOf<SubmitResponse>([
  quizFailedResponseDecoder,
  unsupportedFileFormatResponseDecoder,
  submitSuccessResponseDecoder,
]);

const assignmentDueOutput = (uploads: Uploads): AssignmentDueOutput => ({
  type: 'assignment-due',
  files: uploads
    .map((upload: Upload) => ({
      signed_id: upload.signedId,
      filename: upload.filename,
    }))
    .toArray(),
});

const teamAssignmentOutput = (
  uploads: Maybe<Uploads>,
  submissionParams: TeamDiscussionSubmissionParams,
): TeamDiscussionOutput => ({
  type: 'team-discussion',
  files: uploads.cata({
    Just: (uploads) =>
      uploads
        .map((upload: Upload) => ({
          signed_id: upload.signedId,
          filename: upload.filename,
        }))
        .toArray(),
    Nothing: () => [],
  }),
  team_discussion: {
    title: submissionParams.title.getOrElseValue(''),
    content: submissionParams.content.getOrElseValue(''),
  },
});

const overviewOutput = (embeddedFormFieldOutputs: FormFieldOutput[]): OverviewOutput => ({
  type: 'overview',
  fields: embeddedFormFieldOutputs.map((embeddedFormFieldOutput) => ({
    id: embeddedFormFieldOutput.id,
    value: embeddedFormFieldOutput.value,
  })),
});

const submissionsEncoder = (submissionParams: SegmentSubmissionParams): SubmissionOutput => {
  switch (submissionParams.type) {
    case 'assignment-due':
      return assignmentDueOutput(submissionParams.uploads);
    case 'team-discussion':
      return teamAssignmentOutput(submissionParams.uploads, submissionParams);
    case 'overview':
      submissionParams.embeddedFormFieldOutputs.map((i) => {
        if (!Array.isArray(i.value)) {
          warnAndNotify('badPayload', `The wrong data is sending from`, i);
        }
      });
      return overviewOutput(submissionParams.embeddedFormFieldOutputs);
  }
};

// The learner moved on from a segment that has no next segment open to them: the next
// module is gated, or the course is finished. On the full segment page the learner stays
// on this segment and the page explains what is locked (or returns to the program page
// when nothing is left); the streamlined views hand control back to their host.
const noNextSegment = (
  store: SegmentStore,
  segmentResource: SegmentResource,
  advanceHook: AdvanceHook,
  refused: boolean,
) => {
  switch (segmentResource.payload.presentationStyle) {
    case 'Full':
      store.reachedEndOfAvailableSegments(segmentResource, refused);
      break;
    case 'Embedded Streamlined':
    case 'Streamlined':
      advanceHook(nothing());
      break;
    default:
      assertNever(segmentResource.payload.presentationStyle);
  }
};

const handleMissingLink = (
  store: SegmentStore,
  programId: number,
  error: MissingLinkError,
  advanceHook: AdvanceHook,
) => {
  switch (error.rel) {
    case 'update':
      store.error('This segment cannot be completed at this time.');
      break;
    case 'next':
      store.segmentResource.do((segmentResource: SegmentResource) => {
        noNextSegment(store, segmentResource, advanceHook, false);
      });
      break;
    default:
      store.error('You cannot perform this operation at this time.');
  }
};

const handleBadStatus = (
  store: SegmentStore,
  programId: number,
  error: BadStatus,
  advanceHook: AdvanceHook,
) => {
  switch (error.response.status) {
    case 403:
      // The server refuses the next segment when its module is still gated. If the
      // learner got here with Next, they stay on the segment they just finished.
      if (store.state.kind === 'advancing' || store.state.kind === 'advancing-to') {
        noNextSegment(store, store.state.segmentResource, advanceHook, true);
        break;
      }
      // The learner opened a gated segment directly. The full segment page says when it
      // opens; there is no segment loaded to fall back on.
      if (store.state.kind === 'loading' && store.presentationStyle === 'Full') {
        store.openedUnavailableSegment(true);
        break;
      }
      store.segmentResource.do((segmentResource: SegmentResource) => {
        switch (segmentResource.payload.presentationStyle) {
          case 'Full':
            store.error('You do not have access to this segment yet');
            nav(programRoute(programId));
            break;
          case 'Embedded Streamlined':
          case 'Streamlined':
            advanceHook(nothing());
            break;
          default:
            assertNever(segmentResource.payload.presentationStyle);
        }
      });
      break;
    case 404:
      nav('/404');
      break;
    default:
      handleError(store, error);
  }
};

const handleSubmissionError =
  (store: SegmentStore, programId: number, advanceHook: AdvanceHook) =>
  (error: SubmissionError) => {
    switch (error.kind) {
      case 'missing-link-error':
        handleMissingLink(store, programId, error, advanceHook);
        break;
      case 'item-not-found':
      case 'payload-not-found':
        nav('/404');
        break;
      case 'bad-status':
        handleBadStatus(store, programId, error, advanceHook);
        break;
      default:
        handleError(store, error);
    }
  };

// Fetch the next segment while the store is still 'advancing', so a refusal (a gated
// module) still has the current segment to fall back on.
const advance = (store: SegmentStore, programId: number, advanceHook: AdvanceHook) => {
  Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT('next'))
    .andThen(segmentEndPoint)
    .fork(handleSubmissionError(store, programId, advanceHook), store.loaded);
};

const complete = (store: SegmentStore, programId: number, advanceHook: AdvanceHook) => {
  Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT('update'))
    .andThen(segmentEndPoint)
    .fork(handleSubmissionError(store, programId, advanceHook), store.ready);
};

const completeAndAdvance = (store: SegmentStore, programId: number, advanceHook: AdvanceHook) => {
  Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT('update'))
    .andThen(segmentEndPoint)
    .fork(handleSubmissionError(store, programId, advanceHook), (resource) => {
      findLink('next', resource.links).cata({
        Just: store.advanceTo,
        Nothing: () => {
          noNextSegment(store, resource, advanceHook, false);
        },
      });
    });
};

const submitAndAdvance = (
  store: SegmentStore,
  programId: number,
  segmentSubmissionParams: SegmentSubmissionParams,
  advanceHook: AdvanceHook,
) => {
  Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT('update'))
    .andThen(
      callApi(submitResponseDecoder, {
        submissions: submissionsEncoder(segmentSubmissionParams),
      }),
    )
    .do(() => segmentSubmissionParams.attachmentUploadStore.loading())
    .andThen((response: SubmitResponse) => {
      switch (response.kind) {
        case 'quiz-failed':
          store.segmentResource.cata({
            Just: (sr) => store.quizFailedAction(sr, response.quizResult),
            Nothing: () => {
              warnAndNotify('submitAndAdvance', 'Quiz failed but segmentResource was Nothing', {});
            },
          });
          return Task.succeed<SubmissionError, Maybe<Link>>(nothing());
        case 'unsupported-file-format':
          store.error(
            'This file type is not supported. Please upload a PDF, Word, PowerPoint, Excel, PNG, or JPEG file.',
          );
          return Task.succeed<SubmissionError, Maybe<Link>>(nothing());
        case 'submit-success': {
          const nextLink = findLink('next', response.segmentResource.links);
          if (nextLink.isNothing()) {
            noNextSegment(store, response.segmentResource, advanceHook, false);
          }
          return Task.succeed<SubmissionError, Maybe<Link>>(nextLink);
        }
        default:
          return assertNever(response);
      }
    })
    .fork(handleSubmissionError(store, programId, advanceHook), (maybeLink) =>
      maybeLink.do(store.advanceTo),
    );
};

const advancingTo = (
  store: SegmentStore,
  programId: number,
  direction: SegmentNavigationDirectionType,
  advanceHook: AdvanceHook,
) => {
  Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
    .andThen(findLinkT(direction))
    .andThen(segmentEndPoint)
    .fork(handleSubmissionError(store, programId, advanceHook), (segmentResource) => {
      advanceHook(just(segmentResource));
      store.ready(segmentResource);
    });
};

const reportingResultsFailure =
  (store: SegmentStore, targetOrigin: Maybe<string>) => (err: SubmissionError) => {
    store.saveComplete();
    const iframe: HTMLIFrameElement = document.getElementsByTagName('iframe')[0];

    targetOrigin.do((origin) => {
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage('results-failure', origin);
      }
    });
  };

const reportingResultsSuccess = (store: SegmentStore, targetOrigin: Maybe<string>) => {
  store.saveComplete();
  const iframe: HTMLIFrameElement = document.getElementsByTagName('iframe')[0];

  targetOrigin.do((origin) => {
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage('results-success', origin);
    }
  });
};

class Reactions extends ErrorActionableReaction<SegmentStore, SegmentState, Props> {
  programId = parseInt(this.props.params.programId, 10);
  segmentId = parseInt(this.props.params.segmentId, 10);
  moduleId = parseInt(this.props.params.moduleId, 10);

  tester = () => this.props.store.state;

  loadSegment = (program: Program) => {
    const { store, advanceHook } = this.props;
    Task.succeed<SubmissionError, Program>(program)
      .map((program) =>
        whenActive(program)
          .map((p) => p.modules)
          .getOrElseValue([]),
      )
      .andThen(findItemT(this.moduleId))
      .map((m) => m.segments)
      .andThen(findItemT(this.segmentId))
      .map((s) => s.links)
      .andThen(findLinkT('self'))
      .fork(handleSubmissionError(store, this.programId, advanceHook), store.loading);
  };

  effect = (state: SegmentState) => {
    const { store, advanceHook } = this.props;

    switch (state.kind) {
      case 'loading':
        Task.succeed<SubmissionError, ReadonlyArray<Link>>(store.links)
          .andThen(findLinkT('self'))
          .andThen(segmentEndPoint)
          .fork(handleSubmissionError(store, this.programId, advanceHook), store.loaded);
        break;
      case 'completing':
        complete(store, this.programId, advanceHook);
        break;
      case 'advancing':
        advance(store, this.programId, advanceHook);
        break;
      case 'completing-and-advancing':
        store.kettle.pause();
        completeAndAdvance(store, this.programId, advanceHook);
        break;
      case 'submitting-and-advancing':
        store.kettle.pause();
        submitAndAdvance(store, this.programId, state.submissionParams, advanceHook);
        break;
      case 'previous-advancing':
        store.kettle.pause();
        advancingTo(store, this.programId, 'previous', advanceHook);
        break;
      case 'advancing-to':
        advancingTo(store, this.programId, 'next', advanceHook);
        break;
      case 'loaded':
        store.refreshKettle();
        store.ready(state.segmentResource);
        break;
      case 'waiting': {
        programsStore.resource
          .map(({ payload }) => payload.programs)
          .map<Task<SubmissionError, ProgramResource[]>>(Task.succeed)
          .getOrElse(always(Task.succeed([])))
          .andThen(findPayloadT(this.programId))
          .map((programResource) => programResource.links)
          .andThen(findLinkT('self'))
          .andThen(getProgram)
          .map(({ payload }) => payload)
          .fork(handleSubmissionError(store, this.programId, advanceHook), (program) => {
            // Nothing in a program that has not started is open yet. The full segment
            // page keeps the learner in the course and says when the program opens.
            if (whenUpcoming(program).isJust() && store.presentationStyle === 'Full') {
              store.openedUnavailableSegment(false);
              return;
            }
            this.loadSegment(program);
          });
        break;
      }
      case 'reporting-results':
        store.results.do((results) => {
          Task.succeed<SegmentReactionErrors, ReadonlyArray<Link>>(store.links)
            .andThen(findLinkT('results-create'))
            .andThen(postToApi({ results: results.value }))
            .fork(reportingResultsFailure(store, store.enclosureOrigin), () =>
              reportingResultsSuccess(store, store.enclosureOrigin),
            );
        });
        break;
      case 'ready':
        const { payload } = state.segmentResource;
        // A segment comes back to 'ready' after each submission. Skip a repeat
        // for the same page unless the progress changed.
        if (
          !firstPushFor('program-data', `${window.location.pathname}|${payload.percentComplete}`)
        ) {
          break;
        }
        dataLayerUpdate({
          studentId: payload.studentId,
          studentStatus: payload.registrationStatus,
          registrationType: payload.registrationType,
          programId: payload.id,
          programName: payload.title,
          moduleId: payload.moduleId,
          segmentId: payload.id,
          segmentName: payload.title,
          learningCollectionId: payload.learningCollectionId,
          learningCollectionName: payload.learningCollectionName,
          productLicenseId: payload.productLicenseId,
          productLicenseType: payload.productLicenseType,
          programFamilyId: payload.programFamilyId,
          programFamilyName: payload.programFamilyName,
          schoolPartnerId: payload.schoolPartnerId,
          schoolPartnerName: payload.schoolPartnerName,
          enrollmentOpenDate: payload.enrollmentOpenDate,
          percentComplete: payload.percentComplete,
          percentCompleteBucket: percentCompleteBucket(payload.percentComplete),
          duration: payload.duration,
          startsOn: payload.startsOn,
          endsOn: payload.endsOn,
          programTitle: payload.programTitle,
          programCreatedOn: payload.programCreatedOn,
          event: 'program-data',
        });
        break;
      case 'processing-request':
      case 'error':
      case 'schedule-session':
      case 'quiz-failed':
        break;
      default:
        assertNever(state);
    }
  };
}

export default Reactions;
